import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Sparkles, MailCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { pixelTrackCustom, pixelTrack } from "@/lib/pixel";

const EMPTY = {
  empresa: "", cif: "", nombre: "", apellidos: "", email: "",
  password: "", telefono: "", acepta_condiciones: false, web: "",
};

export default function Registro() {
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState({ terminos_url: null, privacidad_url: null });
  const [enviado, setEnviado] = useState(null); // email al que se envió la verificación
  const [reenviando, setReenviando] = useState(false);

  useEffect(() => {
    api.get("/registro/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.acepta_condiciones) return toast.error("Debes aceptar las condiciones y la privacidad");
    setLoading(true);
    try {
      await api.post("/registro", f);
      pixelTrackCustom("RegistroPendiente");
      pixelTrack("Lead");
      setEnviado(f.email.toLowerCase().trim());
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo completar el registro");
    } finally { setLoading(false); }
  };

  const reenviar = async () => {
    setReenviando(true);
    try {
      await api.post("/registro/reenviar", { email: enviado });
      toast.success("Te hemos reenviado el correo de verificación");
    } catch { toast.error("No se pudo reenviar ahora mismo"); }
    finally { setReenviando(false); }
  };

  if (enviado) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F8FA] px-6" data-testid="registro-revisa-correo">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto h-16 w-16 rounded-full bg-[#FF5625]/10 flex items-center justify-center mb-6">
            <MailCheck size={30} className="text-[#FF5625]" />
          </div>
          <h2 className="text-2xl font-black tracking-tight text-[#0E2A47]">Revisa tu correo</h2>
          <p className="text-[#0E2A47]/70 mt-3">
            Te hemos enviado un enlace de verificación a{" "}
            <span className="font-bold text-[#0E2A47]" data-testid="registro-email-enviado">{enviado}</span>.
            Pulsa el enlace para activar tu cuenta y empezar tu prueba de 14 días.
          </p>
          <div className="mt-5 rounded-lg bg-[#FFF7ED] border border-[#FF5625]/20 p-3 text-sm text-[#0E2A47]/70">
            ¿No lo ves? Mira en <b>Spam / Promociones</b>. El enlace caduca en 48&nbsp;horas.
          </div>
          <Button onClick={reenviar} disabled={reenviando} data-testid="registro-reenviar"
            className="w-full mt-6 bg-[#0E2A47] hover:bg-[#163C65] text-white font-semibold">
            {reenviando ? <><Loader2 size={16} className="mr-2 animate-spin" />Reenviando…</> : "Reenviar correo de verificación"}
          </Button>
          <div className="mt-6 text-sm text-[#0E2A47]/60">
            ¿Ya lo verificaste? <Link to="/login" className="text-[#FF5625] font-semibold" data-testid="registro-ir-login">Inicia sesión</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#F7F8FA]">
      <div className="hidden lg:flex w-1/2 relative bg-[#0E2A47]">
        <img src="https://images.unsplash.com/photo-1551884170-09fb70a3a2ed?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Planta alimentaria" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <ShieldCheck className="text-[#FF5625]" size={32} />
            <div className="leading-none">
              <span className="font-black text-2xl tracking-tighter">AuditReady<span className="text-[#FF5625]">.</span></span>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mt-1">by Agentik</div>
            </div>
          </div>
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#FF5625] mb-4"><Sparkles size={14} /> 14 días gratis</div>
            <h1 className="text-4xl font-black tracking-tighter leading-tight">Empieza tu Camino IFS hoy</h1>
            <p className="mt-4 text-white/70 max-w-md">Prueba el producto completo (niveles 1-4) durante 14 días. Sin tarjeta. Tus datos se conservan al terminar.</p>
          </div>
          <div className="text-xs text-white/40">Núcleo Agentik · Multi-tenant seguro</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="registro-form">
          <div className="lg:hidden mb-6">
            <div className="flex items-center gap-2">
              <ShieldCheck className="text-[#FF5625]" size={28} />
              <span className="font-black text-xl tracking-tighter text-[#0E2A47]">AuditReady<span className="text-[#FF5625]">.</span></span>
            </div>
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-[#0E2A47]">Crea tu cuenta</h2>
          <p className="text-sm text-[#0E2A47]/60 mt-1 mb-6">Prueba gratuita de 14 días · niveles 1-4 completos.</p>

          {/* Honeypot invisible */}
          <input type="text" name="web" value={f.web} onChange={(e) => set({ web: e.target.value })}
            tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Fld label="Empresa"><Input value={f.empresa} onChange={(e) => set({ empresa: e.target.value })} required data-testid="reg-empresa" /></Fld>
              <Fld label="CIF"><Input value={f.cif} onChange={(e) => set({ cif: e.target.value })} required data-testid="reg-cif" /></Fld>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Fld label="Nombre"><Input value={f.nombre} onChange={(e) => set({ nombre: e.target.value })} required data-testid="reg-nombre" /></Fld>
              <Fld label="Apellidos"><Input value={f.apellidos} onChange={(e) => set({ apellidos: e.target.value })} data-testid="reg-apellidos" /></Fld>
            </div>
            <Fld label="Email"><Input type="email" value={f.email} onChange={(e) => set({ email: e.target.value })} required data-testid="reg-email" placeholder="tu@empresa.com" /></Fld>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Fld label="Contraseña"><Input type="password" value={f.password} onChange={(e) => set({ password: e.target.value })} required data-testid="reg-password" placeholder="mín. 6 caracteres" /></Fld>
              <Fld label="Teléfono (opcional)"><Input value={f.telefono} onChange={(e) => set({ telefono: e.target.value })} data-testid="reg-telefono" /></Fld>
            </div>
            <label className="flex items-start gap-2 text-xs text-[#0E2A47]/70 pt-1">
              <input type="checkbox" checked={f.acepta_condiciones} onChange={(e) => set({ acepta_condiciones: e.target.checked })} data-testid="reg-acepta" className="mt-0.5" />
              <span>
                Acepto los {cfg.terminos_url
                  ? <a href={cfg.terminos_url} target="_blank" rel="noreferrer" className="text-[#FF5625] underline" data-testid="reg-link-terminos">Términos del Servicio</a>
                  : <span className="font-medium text-[#0E2A47]/80">Términos del Servicio</span>}
                {" "}y la {cfg.privacidad_url
                  ? <a href={cfg.privacidad_url} target="_blank" rel="noreferrer" className="text-[#FF5625] underline" data-testid="reg-link-privacidad">Política de Privacidad</a>
                  : <span className="font-medium text-[#0E2A47]/80">Política de Privacidad</span>}
                {" "}de AuditReady.
              </span>
            </label>
          </div>

          <Button type="submit" disabled={loading} data-testid="reg-submit"
            className="w-full mt-6 bg-[#FF5625] hover:bg-[#E0451A] text-white font-semibold active:scale-[0.98] transition-transform">
            {loading ? "Creando cuenta…" : "Empezar prueba gratis"}
          </Button>
          <div className="mt-6 text-sm text-[#0E2A47]/60 text-center">
            ¿Ya tienes cuenta? <Link to="/login" className="text-[#FF5625] font-semibold" data-testid="link-login">Inicia sesión</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Fld({ label, children }) {
  return (<div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">{label}</Label><div className="mt-1.5">{children}</div></div>);
}
